import { Typography, useEventCallback } from '@mui/material';
import { ResearchNotifications } from 'notifications-from-civil-status/view-models/notifications-list';
import { Fragment, ReactElement, useEffect, useMemo, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { __ACT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponse } from 'shell/models/http-response';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import AccordionFilter from 'civil-status/components/research-communication-from-list/filter';
import { isObjectEmpty } from 'shell/utils/is-object-empty';
import { useParams } from 'react-router-dom';
import { CommunicationsResearchFromListCrumbs } from 'civil-status/constants/breadcrumb';
import {
  CommunicationList,
  ResearchCommunicationFromList,
} from 'civil-status/view-models/communication-from-list';
import { transformResearchCommunicationFromList } from 'civil-status/utils/transformer-communication-from-list/transform-research-communication-from-list';
import { transformCommunicationList } from 'civil-status/utils/transformer-communication-from-list/transform-communication-list';
import TableCommunications from 'civil-status/components/research-communication-from-list/table';
import { ArchivioAnnotazioni } from 'civil-status/view-models/archive-annotations-list';
import { CodeScope } from 'civil-status/constants/code-scope';

const PageResearchCommunicationFormList = (): ReactElement => {
  const filterTable = useFilterTable<ResearchNotifications>();

  const params = useParams();

  // filtri
  const [open, setOpen] = useState<boolean>(true);
  // filtri selezionati
  const [filterSelected, setFilterSelected] = useState<ResearchNotifications | undefined>({});
  const [tableProps, setTableProps] = useState<Record<string, number>>({
    page: 0,
    rowsPerPage: 10,
  });

  const [communicationsList, getCommunicationsList] = useHttpFetch<
    HttpResponse<Array<ArchivioAnnotazioni>>,
    Array<CommunicationList>
  >(`${__ACT_API_URL__}/archivio`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'GET',
      loading: true,
      data: {},
    },
    transformer: transformCommunicationList,
  });

  const handleResearch = useEventCallback(
    (values: ResearchCommunicationFromList, page?: number, rowsPerPage?: number) => {
      const newFilters: ResearchCommunicationFromList = {
        typeEvent: values.typeEvent,
        code: values.code,
        dateEnd: values.dateEnd,
        dateStart: values.dateStart,
        state: values.state,
        scope: values.scope,
        codOperator: values.codOperator,
        tableProps: {
          page,
          rowsPerPage,
        },
      };

      // mantenere dati
      setFilterSelected(newFilters);
      filterTable.setValues(newFilters);

      const queryString = transformResearchCommunicationFromList(newFilters, page, rowsPerPage);

      setTableProps({
        page: page || 0,
        rowsPerPage: rowsPerPage || 10,
      });

      getCommunicationsList({
        params: {},
        paramsSerializer: () => queryString,
      });
    }
  );

  const handleSubmitResearch = useEventCallback((values: ResearchCommunicationFromList) => {
    handleResearch(values);
  });

  useEffect(() => {
    if (((communicationsList.data as Array<CommunicationList>) || []).length) {
      // se abbiamo risultato chiudere accordion dei filtri
      setOpen(false);
    }
  }, [communicationsList.data]);

  useEffect(() => {
    if (!isObjectEmpty((filterTable.values || {}) as Record<string, unknown>)) {
      handleResearch(
        filterTable.values || {},
        filterTable.values?.tableProps?.page || 0,
        filterTable.values?.tableProps?.rowsPerPage || 10
      );
    }
  }, []);

  const idCat = useMemo(() => {
    return params.idCat || '';
  }, [params.idCat]);

  const items = useMemo(() => {
    if (params.valueCat && idCat) {
      return CommunicationsResearchFromListCrumbs(params.valueCat, idCat);
    }

    return [];
  }, [params.valueCat, idCat]);

  return (
    <Fragment>
      <Breadcrumbs items={items} />
      <div className="pmi-pt-4 pmi-pb-3 pmi-d-flex pmi-align-items-center pmi-justify-content-between">
        <Typography
          color="secondary.main"
          style={{
            fontSize: '26px',
          }}
        >
          Ricerca Comunicazioni da Lista
        </Typography>
      </div>
      <div className="circle" />
      <AccordionFilter
        setOpen={setOpen}
        open={open}
        handleSubmitResearch={handleSubmitResearch}
        idCat={idCat}
        scope={CodeScope.COMUNIC}
      />
      {communicationsList.data ? (
        ((communicationsList.data as Array<CommunicationList>) || [])?.length > 0 ? (
          <TableCommunications
            data={communicationsList.data as Array<CommunicationList>}
            filterSelected={filterSelected}
            getCommunicationListData={handleResearch}
            tableProps={tableProps}
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

PageResearchCommunicationFormList.displayName = 'Page Research Communication Form List component';

export default PageResearchCommunicationFormList;
